import { useLayoutEffect, useMemo, useState } from 'react';
import throttle from 'lodash.throttle';
import { RC_SLIDER_RAIL_SELECTOR } from '../components/gradient-stops-slider/constants';

const RESIZE_OBSERVER_THROTTLE_MS = 100;

/**
 * Returns the arrow position (px) for the color-stop panel.
 *
 * Coordinate system is relative to the panel left edge and is derived from real DOM
 * offsets between:
 * - ws-ui-gradient-picker root
 * - gradient-stops-slider container
 * - color-stop-control-panel container
 *
 * Measures on mount and when observed layout boxes change (ResizeObserver, throttled).
 */
export function useColorStopPanelArrowPosition(params) {
  const { pickerRef, sliderRef, panelRef, positionPercent } = params;

  const [layout, setLayout] = useState();

  useLayoutEffect(() => {
    const pickerElement = pickerRef.current;
    const sliderElement = sliderRef.current;
    const panelElement = panelRef.current;

    if (!pickerElement || !sliderElement || !panelElement) {
      return undefined;
    }

    const measure = () => {
      const pickerRect = pickerElement.getBoundingClientRect();
      const panelRect = panelElement.getBoundingClientRect();

      const panelLeftFromPicker = panelRect.left - pickerRect.left;

      const railElement = sliderElement.querySelector(RC_SLIDER_RAIL_SELECTOR);
      const sliderRect = sliderElement.getBoundingClientRect();
      const railRect =
        (railElement instanceof HTMLElement ? railElement.getBoundingClientRect() : null) ??
        sliderRect;

      const railLeftFromPicker = railRect.left - pickerRect.left;

      setLayout({
        panelToRailLeft: railLeftFromPicker - panelLeftFromPicker,
        railWidth: railRect.width,
      });
    };

    const throttledMeasure = throttle(measure, RESIZE_OBSERVER_THROTTLE_MS);

    measure();

    const resizeObserver = new ResizeObserver(() => {
      throttledMeasure();
    });

    resizeObserver.observe(pickerElement);

    return () => {
      throttledMeasure.cancel();
      resizeObserver.disconnect();
    };
  }, [panelRef, pickerRef, sliderRef]);

  return useMemo(() => {
    if (positionPercent == null || !layout || layout.railWidth <= 0) {
      return 0;
    }

    const positionPx = (positionPercent / 100) * layout.railWidth;

    return layout.panelToRailLeft + positionPx;
  }, [layout, positionPercent]);
}
